/**
 * TT-02 — pure formatting for the time-tracking surface. No React, no MUI, no
 * network: everything here is unit-tested rather than rendered.
 *
 * Durations are MINUTES on the wire (contract TT-D7 — integers so nothing
 * accumulates float error before the review table multiplies by a price) and
 * HOURS on screen, because that is the unit an invoice line is written in.
 */

/** `315` → `"5.3 h"`. One decimal — the mockup's unit, and enough for a rate. */
export function formatHours(minutes: number | null | undefined): string {
  if (minutes == null) return "—";
  return `${(minutes / 60).toFixed(1)} h`;
}

/** `315` → `"5:15"` — the wall-clock reading, for a row that shows both ends. */
export function formatDurationHm(minutes: number | null | undefined): string {
  if (minutes == null) return "—";
  const abs = Math.max(0, Math.round(minutes));
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${String(h)}:${String(m).padStart(2, "0")}`;
}

/**
 * Minutes between two ISO instants, or `null` when either is missing/unparsable.
 * Drives the form's live duration preview, so it must never throw on a
 * half-typed datetime.
 */
export function minutesBetween(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
): number | null {
  if (!startIso || !endIso) return null;
  const a = Date.parse(startIso);
  const b = Date.parse(endIso);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 60_000);
}

/** `"2026-08-11T09:30:00Z"` → `"2026-08-11 09:30"` in the viewer's zone. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n: number): string => String(n).padStart(2, "0");
  return (
    `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** Just the clock part — the second column of a start/end pair. */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * An ISO instant as the value a `datetime-local` input wants
 * (`YYYY-MM-DDTHH:mm`, **local** time, no zone suffix).
 *
 * The conversion is deliberate rather than a `.toISOString().slice(0,16)`:
 * that slices the UTC rendering and silently shifts a German pilot's 09:30
 * to 07:30 in the field.
 */
export function toDateTimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number): string => String(n).padStart(2, "0");
  return (
    `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** The inverse — a `datetime-local` value back to a UTC ISO instant. */
export function fromDateTimeLocalValue(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Month bounds as `[from, to)` ISO instants — the period filter's unit. */
export function monthRange(
  year: number,
  monthIndex0: number,
): {
  from: string;
  to: string;
} {
  const from = new Date(year, monthIndex0, 1, 0, 0, 0, 0);
  const to = new Date(year, monthIndex0 + 1, 1, 0, 0, 0, 0);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** `"2026-08"` — the `<input type="month">` value for a `[from,to)` pair. */
export function monthInputValue(from: string | undefined): string {
  if (!from) return "";
  const d = new Date(from);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** `"2026-08"` → `[from, to)`. Returns `null` on anything else. */
export function monthValueToRange(
  value: string,
): { from: string; to: string } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return monthRange(year, month - 1);
}

/**
 * `145` + `"EUR"` → `"145.00 €"`. Falls back to the raw code for currencies
 * `Intl` cannot symbolise, rather than throwing inside a totals bar.
 */
export function formatMoney(
  amount: number | null | undefined,
  currency: string,
): string {
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}
